import React, { useEffect, useCallback, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import DividerWithText from "Common/components/DividerLineWithText";
import { track } from 'statsig';
import { STATSIG } from 'statsig/events';

// MSAL 配置
const msalConfig = {
    auth: {
        clientId: '84c5df08-e712-4f08-9d68-64daf092dc10',
        // common, so self-serve users on personal Microsoft accounts keep
        // working. Confining an SSO customer to its own directory is the
        // backend's tid check, not this endpoint.
        authority: 'https://login.microsoftonline.com/common',
        redirectUri:
            typeof window !== 'undefined' ? window.location.origin + '/login' : '',
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case 0: // LogLevel.Error
                        console.error(message);
                        return;
                    case 1: // LogLevel.Info
                        console.info(message);
                        return;
                    case 2: // LogLevel.Verbose
                        console.debug(message);
                        return;
                    case 3: // LogLevel.Warning
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

// Sign-in only. The backend verifies the ID token locally and reads identity
// from its claims, so no Microsoft Graph access is requested.
const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
};

function OutlookSignIn({ onSuccess, onError, onToSignUp, setLoading }) {
    const [msalInstance, setMsalInstance] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);

    // 处理Outlook登录成功
    const handleOutlookCallback = useCallback(
        async (response) => {
            setLoading(true);
            try {
                // 检查响应是否存在
                if (!response) {
                    throw new Error('No response from Outlook');
                }

                sessionStorage.setItem('response', JSON.stringify(response));

                // 检查Outlook返回的响应结构
                if (!response.idToken) {
                    throw new Error('Missing idToken from Outlook response');
                }

                // 调用后端API进行Outlook登录验证
                // 后端本地验签 idToken 并校验租户，不再依赖 Graph
                const result = await fetch('/api/v1/los/auth/outlookSignin', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        idToken: response.idToken,
                    }),
                });

                if (result.status !== 200) {
                    throw new Error('Outlook login failed');
                }

                const data = await result.json();
                if (data.code !== 0) {
                    const error = new Error(data.errMessage || 'Outlook login failed');
                    // 后端给出的业务原因（如账号被禁用）应原样展示给用户，
                    // 与 Strata 的提示保持一致；标记出来让错误处理区分
                    // “服务端明确拒绝” 和 “本地/网络异常”。
                    error.serverMessage = data.errMessage || '';
                    throw error;
                }

                // 处理返回的数据数组
                const companyDataList = data.data || [];

                // 存储用户邮箱信息
                const userEmail = (response.user?.mail || response.user?.userPrincipalName)
                    ?.toLowerCase()
                    .trim() || '';
                
                if (companyDataList.length === 0) {
                    if(onToSignUp) {
                        track(STATSIG.AUTH.LOGIN_FAIL, 'outlook', {
                            type: 'outlook',
                            reason: 'account_not_found',
                        });
                        return onToSignUp(userEmail); // 未注册的逻辑
                    }
                    // 如果数据为空数组，表示账户未找到，应该显示账户未找到的提示
                    const error = new Error('Account not found');
                    error.isAccountNotFound = true;
                    throw error;
                }

                sessionStorage.setItem('userEmail', userEmail);

                if (response.user?.displayName) {
                    const nameParts = response.user.displayName.split(' ');
                    sessionStorage.setItem('userFirstName', nameParts[0]);
                    if (nameParts.length > 1) {
                        sessionStorage.setItem(
                            'userLastName',
                            nameParts.slice(1).join(' ')
                        );
                    }
                }

                if (onSuccess) {
                    onSuccess({
                        companyDataList,
                        email: userEmail
                    });
                }
            } catch (error) {
                console.error('Outlook login failed:', error);
                if (onError) {
                    onError(error);
                }
            }finally{
                setLoading(false);
            }
        },
        [onSuccess, onError]
    );

    // 初始化Outlook登录
    useEffect(() => {
        const initOutlookSignIn = async () => {
            try {
                console.log('Starting Outlook Sign In initialization...');
                console.log('MSAL PublicClientApplication available:', !!PublicClientApplication);
                console.log('MSAL config:', msalConfig);
                
                // 使用静态导入的 MSAL
                const instance = new PublicClientApplication(msalConfig);
                console.log('MSAL instance created:', instance);
                
                // 尝试初始化 MSAL 实例（2.0.0 版本可能不需要）
                try {
                    if (typeof instance.initialize === 'function') {
                        await instance.initialize();
                        console.log('MSAL instance initialized successfully');
                    } else {
                        console.log('MSAL instance does not require initialization');
                    }
                } catch (initError) {
                    console.warn('MSAL initialization failed, but continuing:', initError);
                }
                
                setMsalInstance(instance);
                setIsInitializing(false);
                console.log(
                    'MSAL instance created and initialized successfully:',
                    instance
                );
            } catch (error) {
                console.error('Outlook Sign In initialization failed:', error);
                console.error('Error details:', error.message, error.stack);
                console.error('Error name:', error.name);
                console.error('Error constructor:', error.constructor.name);
                setIsInitializing(false);
            }
        };

        initOutlookSignIn().catch(error => {
            console.error('Failed to initialize Outlook Sign In:', error);
        });
    }, []);

    // 处理Outlook登录按钮点击
    const handleOutlookSignIn = async () => {
        try {
            if (!msalInstance) {
                throw new Error('MSAL instance not initialized');
            }

            // 使用 MSAL 进行登录
            const loginResponse = await msalInstance.loginPopup(loginRequest);

            if (loginResponse && loginResponse.idToken) {
                // 身份信息直接来自 ID token 的 claims，无需再调 Graph
                const claims = loginResponse.idTokenClaims || {};
                await handleOutlookCallback({
                    idToken: loginResponse.idToken,
                    user: {
                        mail: claims.email,
                        userPrincipalName: claims.upn || claims.preferred_username,
                        displayName: claims.name,
                    },
                });
            } else {
                throw new Error('Login failed - no ID token received');
            }
        } catch (error) {
            console.error('Outlook Sign In failed:', error);
            if (onError) {
                onError(error);
            }
        }
    };

    return (
        <div className="outlook-signin-container" style={{
            width: '100%',
            maxWidth: window.innerWidth <= 768 ? '320px' : '400px',
            margin: '0 auto'
        }}>
            <button
                type="button"
                onClick={handleOutlookSignIn}
                disabled={!msalInstance || isInitializing}
                className="outlook-signin-button"
                style={{
                    width: '100%',
                    height: '40px',
                    backgroundColor: (msalInstance && !isInitializing) ? '#fff' : '#d9d9d9',
                    border: `1px solid ${(msalInstance && !isInitializing) ? '#dadce0' : '#d9d9d9'}`,
                    color: (msalInstance && !isInitializing) ? '#222' : '#999',
                    borderRadius: '24px',
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Inter',
                    fontWeight: '400',
                    cursor: msalInstance ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                    if (msalInstance) {
                        e.currentTarget.style.backgroundColor = '#f7f8ff';
                        e.currentTarget.style.border = '1px solid #d8eaff';
                    }
                }}
                onMouseOut={(e) => {
                    if (msalInstance) {
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.border = '1px solid #dadce0';
                    }
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                >
                    <g clipPath="url(#clip0_5018_12726)">
                        <path
                            d="M13.4573 21.9998H13.1546C13.1262 21.9873 13.099 21.9698 13.0695 21.9632C12.8734 21.9206 12.6767 21.8802 12.4803 21.8395C11.0645 21.5446 9.64872 21.2496 8.23292 20.9549C6.62954 20.6212 5.02588 20.2872 3.42222 19.9551C2.44852 19.7533 1.47426 19.5542 0.5 19.354C0.5 13.726 0.5 8.09801 0.5 2.47027C0.769112 2.41892 1.03795 2.36785 1.30706 2.31624C2.51738 2.08383 3.72769 1.85115 4.93801 1.61874C6.04531 1.406 7.15261 1.19298 8.25991 0.980513C9.32727 0.775689 10.3949 0.572231 11.4623 0.367407C12.0853 0.247789 12.7084 0.126534 13.3312 0.00500486C13.4113 -0.0105618 13.4562 0.00746275 13.4515 0.0978584C13.4488 0.147835 13.4515 0.197812 13.4515 0.247789C13.4515 7.42127 13.4515 14.595 13.4518 21.7685C13.4518 21.8458 13.4557 21.9228 13.4579 22.0001L13.4573 21.9998ZM10.0305 10.8716C10.0242 10.7793 10.0115 10.6209 10.0027 10.4622C9.97209 9.91411 9.86687 9.38075 9.63632 8.88098C9.189 7.91148 8.50038 7.23829 7.37077 7.13916C6.78021 7.08727 6.22022 7.19569 5.71367 7.50921C5.05921 7.91421 4.65375 8.51639 4.40116 9.2229C4.10726 10.0452 4.05327 10.8951 4.14362 11.7586C4.20918 12.3854 4.38078 12.9769 4.71435 13.5163C5.46054 14.7236 6.65598 15.0784 7.82911 14.7499C8.60422 14.533 9.12068 13.9986 9.49529 13.3169C9.90268 12.5757 10.0134 11.7668 10.0305 10.871V10.8716Z"
                            fill="#0072C6"
                        />
                        <path
                            d="M14.3188 10.6231C14.4869 10.7365 14.6262 10.8299 14.7648 10.9241C15.0405 11.1117 15.3162 11.2996 15.5917 11.4877C15.8374 11.6554 15.9575 11.6571 16.2046 11.494C18.2555 10.1397 20.3068 8.78544 22.3578 7.43114C22.3955 7.40629 22.4341 7.38308 22.4969 7.34375C22.4969 7.41066 22.4969 7.45763 22.4969 7.5046C22.4969 10.1449 22.4969 12.7852 22.4969 15.4256C22.4969 15.9379 22.0914 16.3787 21.5739 16.4226C21.3915 16.4382 21.2081 16.4467 21.0252 16.4469C18.8527 16.4486 16.68 16.448 14.5075 16.448C14.3819 16.448 14.319 16.3862 14.3188 16.2626C14.3188 14.4449 14.3188 12.6271 14.3188 10.8094V10.6234V10.6231Z"
                            fill="#0072C6"
                        />
                        <path
                            d="M18.1013 4.66371C19.3026 4.66371 20.5038 4.66371 21.7047 4.66371C22.0474 4.66371 22.4451 4.99908 22.4942 5.33035C22.5137 5.4628 22.4876 5.56248 22.3559 5.6496C20.9878 6.55519 19.6251 7.46898 18.2611 8.38058C17.5391 8.86315 16.8161 9.3438 16.0972 9.83074C15.96 9.92359 15.8471 9.92304 15.7088 9.83401C15.294 9.56665 14.8745 9.30639 14.4528 9.04995C14.3558 8.99096 14.3161 8.92678 14.3164 8.81126C14.3205 7.484 14.3208 6.15674 14.3164 4.82921C14.3159 4.69976 14.3495 4.65962 14.4842 4.66016C15.6898 4.66535 16.8957 4.66317 18.1013 4.66317V4.66371Z"
                            fill="#0072C6"
                        />
                        <path
                            d="M8.5666 11.0178C8.56494 11.5313 8.51178 12.0349 8.2939 12.507C8.10742 12.9115 7.83391 13.2365 7.37418 13.3389C6.82714 13.461 6.38257 13.2862 6.049 12.838C5.7832 12.4805 5.65787 12.0684 5.60636 11.6367C5.53529 11.0427 5.54438 10.449 5.71378 9.86863C5.82507 9.48766 6.00163 9.1411 6.31481 8.88002C6.79712 8.47774 7.67139 8.48566 8.11817 9.16486C8.35423 9.52371 8.46826 9.92162 8.51949 10.3392C8.54704 10.5639 8.55145 10.7914 8.5666 11.0178Z"
                            fill="white"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_5018_12726">
                            <rect
                                width="22"
                                height="22"
                                fill="white"
                                transform="translate(0.5)"
                            />
                        </clipPath>
                    </defs>
                </svg>
                <span style={{ minWidth: onToSignUp ? 190 : 177, textAlign: 'left'}}>{isInitializing ? 'Loading Outlook...' : (msalInstance ? onToSignUp ? 'Continue with Outlook' : 'Sign in with Outlook' : 'Outlook not available')}</span>
            </button>
            <DividerWithText text="OR" />
        </div>
    );
}

export default OutlookSignIn;
